// Shared parser: preview and grading use the same syntax tree.
export function parseMathExpression(raw) {
  if (typeof raw !== 'string' || raw.length > 1000) return null;
  const s = raw.replace(/\u200b/g, '').replace(/[−–—]/g, '-').replace(/,/g, '.').replace(/\\cdot|[·×]/g, '*').replace(/\\pi|π/gi, 'pi').replace(/\\sqrt\s*\[3\]|sqrt\s*\[3\]|∛/gi, 'cbrt').replace(/\\sqrt|√/gi, 'sqrt').replace(/\{/g, '(').replace(/\}/g, ')');
  const ts = [];
  let p = 0;
  while (p < s.length) {
    const gap = /^\s*/.exec(s.slice(p))[0];
    p += gap.length;
    if (p === s.length) break;
    const m = /^(\d+(?:\.\d+)?|\.\d+|sqrt|cbrt|pi|[+\-*/^()])/i.exec(s.slice(p));
    if (!m) return null;
    ts.push({
      t: m[0].toLowerCase(),
      gap: !!gap
    });
    p += m[0].length;
  }
  let i = 0;
  const n = (k, a, b) => ({
    k,
    a,
    b
  });
  const peek = () => ts[i]?.t;
  const isAtom = () => /^(?:\d|\.|pi|sqrt|cbrt|\()/.test(peek() || '');
  function atom() {
    const t = ts[i++];
    if (!t) throw Error();
    if (/^(\d|\.)/.test(t.t)) {
      const whole = n('number', t.t);
      if (ts[i]?.gap && /^\d+$/.test(t.t) && /^\d+$/.test(peek() || '') && ts[i + 1]?.t === '/' && /^\d+$/.test(ts[i + 2]?.t || '')) {
        const b = ts[i++].t;
        i++;
        return n('mixed', whole, n('/', n('number', b), n('number', ts[i++].t)));
      }
      return whole;
    }
    if (t.t === 'pi') return n('pi');
    if (t.t === '(') {
      const a = sum();
      if (peek() !== ')') throw Error();
      i++;
      return n('group', a);
    }
    if (t.t === 'sqrt' || t.t === 'cbrt') return n(t.t, atom());
    throw Error();
  }
  function power() {
    let a = atom();
    if (peek() === '^') {
      i++;
      a = n('^', a, unary());
    }
    return a;
  }
  function unary() {
    if (peek() === '+' || peek() === '-') {
      const op = ts[i++].t;
      return n('u' + op, unary());
    }
    return power();
  }
  function product() {
    let a = unary();
    while (peek() === '*' || peek() === '/' || isAtom()) {
      if (peek() === '*') {
        i++;
        a = n('*', a, unary());
      } else if (peek() === '/') {
        i++;
        let b = unary();
        while (isAtom() && !ts[i].gap) b = n('implicit', b, unary());
        a = n('/', a, b);
      } else a = n('implicit', a, unary());
    }
    return a;
  }
  function sum() {
    let a = product();
    while (peek() === '+' || peek() === '-') {
      const op = ts[i++].t;
      a = n(op, a, product());
    }
    return a;
  }
  try {
    const tree = sum();
    return i === ts.length ? tree : null;
  } catch {
    return null;
  }
}
function evaluate(n) {
  const a = () => evaluate(n.a),
    b = () => evaluate(n.b);
  switch (n.k) {
    case 'number':
      return Number(n.a);
    case 'pi':
      return Math.PI;
    case 'group':
      return a();
    case 'mixed':
    case '+':
      return a() + b();
    case '-':
      return a() - b();
    case '*':
    case 'implicit':
      return a() * b();
    case '/':
      return a() / b();
    case '^':
      return a() ** b();
    case 'u-':
      return -a();
    case 'u+':
      return a();
    case 'sqrt':
      return Math.sqrt(a());
    case 'cbrt':
      return Math.cbrt(a());
    default:
      return NaN;
  }
}
function latex(n) {
  const a = () => latex(n.a),
    b = () => latex(n.b);
  switch (n.k) {
    case 'number':
      return n.a.replace('.', '{,}');
    case 'pi':
      return '\\pi';
    case 'group':
      return '\\left(' + a() + '\\right)';
    case 'mixed':
      return a() + '\\,' + b();
    case 'sqrt':
    case 'cbrt':
      return '\\sqrt' + (n.k === 'cbrt' ? '[3]' : '') + '{' + (n.a.k === 'group' ? latex(n.a.a) : a()) + '}';
    case '/':
      return '\\dfrac{' + (n.a.k === 'group' ? latex(n.a.a) : a()) + '}{' + (n.b.k === 'group' ? latex(n.b.a) : b()) + '}';
    case '^':
      return '{' + a() + '}^{' + b() + '}';
    case '*':
      return a() + '\\cdot ' + b();
    case 'implicit':
      return a() + '\\,' + b();
    case 'u-':
      return '-' + a();
    case 'u+':
      return '+' + a();
    default:
      return a() + n.k + b();
  }
}
export function numericMathExpression(raw) {
  const tree = parseMathExpression(raw);
  if (!tree) return null;
  const v = evaluate(tree);
  return Number.isFinite(v) ? v : null;
}
export function previewMathExpression(raw) {
  const tree = parseMathExpression(raw);
  return tree ? latex(tree) : null;
}
